import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Modal,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { KeyboardArrowDown, Search, Close, PictureAsPdf } from "@mui/icons-material";
import useVendorAccessGate from "../../../hooks/useVendorAccessGate";
import { useAuth } from "../../../services/providers/authProvider";
import { httpClient } from "../../../services/httpClient";
import api from "../../../config/gatewayConfig";

const Fatturazione = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [orderAmount, setOrderAmount] = useState(""); // "asc" or "desc"

  useVendorAccessGate();
  const { vendorId } = useAuth() || {};

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!vendorId) return;
      try {
        const response = await httpClient.get(api.getVendorInvoices(vendorId));
        setInvoices(response.data || []);
      } catch (error) {
        console.error("Errore nel recupero delle fatture:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [vendorId]);

  const filteredInvoices = invoices.filter((inv) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;

    return (
      String(inv.ecommerce ?? "").toLowerCase().includes(q) ||
      String(inv.numeroFattura ?? "").toLowerCase().includes(q)
    );
  });

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    if (!orderAmount) return 0;
    // parse amount like "50.00 €" to 50.00
    const valA = parseFloat(a.importo.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
    const valB = parseFloat(b.importo.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
    
    if (orderAmount === "asc") return valA - valB;
    if (orderAmount === "desc") return valB - valA;
    return 0;
  });

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%", fontFamily: "Rubik, sans-serif" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h4" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold", color: "#0b3d87" }}>
            Storico Fatturazioni
          </Typography>
          <Box display="flex" alignItems="center" gap={2} sx={{ width: { xs: "100%", sm: "40%" } }}>
            <TextField
              variant="outlined"
              placeholder="Cerca n. fattura o ecommerce..."
              size="small"
              sx={{ fontFamily: "Rubik, sans-serif", backgroundColor: "white", borderRadius: 2, width: "100%" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: <Search sx={{ color: "#0b3d87" }} />,
                }
              }}
            />
          </Box>
        </Box>

        <Typography variant="subtitle1" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold", color: "#0b3d87", mt: 2 }}>
          FILTRA E ORDINA
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          {["Data inizio intervallo", "Data fine intervallo"].map(
            (filter) => (
              <Button
                key={filter}
                variant="contained"
                endIcon={<KeyboardArrowDown />}
                sx={{
                  fontFamily: "Rubik, sans-serif",
                  backgroundColor: "#0b3d87",
                  color: "white",
                  borderRadius: "7px",
                  padding: "6px 15px",
                  fontWeight: "bold",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#092b6a" },
                }}
              >
                {filter}
              </Button>
            )
          )}
          <FormControl size="small" sx={{ minWidth: 200, backgroundColor: "white", borderRadius: "7px" }}>
            <InputLabel sx={{ fontFamily: "Rubik, sans-serif" }}>Ordina per Importo</InputLabel>
            <Select
              value={orderAmount}
              label="Ordina per Importo"
              onChange={(e) => setOrderAmount(e.target.value)}
              sx={{ fontFamily: "Rubik, sans-serif", borderRadius: "7px" }}
            >
              <MenuItem value=""><em style={{fontFamily: "Rubik, sans-serif"}}>Nessun ordinamento</em></MenuItem>
              <MenuItem value="asc" sx={{fontFamily: "Rubik, sans-serif"}}>Crescente</MenuItem>
              <MenuItem value="desc" sx={{fontFamily: "Rubik, sans-serif"}}>Decrescente</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Typography sx={{ fontFamily: "Rubik, sans-serif", textAlign: "center", py: 5 }}>
              Caricamento fatture in corso...
            </Typography>
          ) : sortedInvoices.length > 0 ? (
            <Grid container spacing={3}>
              {sortedInvoices.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={item.numeroFattura || index}>
                  <Card sx={{ borderRadius: "12px", boxShadow: "0px 4px 20px rgba(11, 61, 135, 0.05)", height: "100%", display: "flex", flexDirection: "column" }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="h6" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold", color: "#0b3d87" }}>
                          {item.ecommerce}
                        </Typography>
                        <Typography sx={{ fontFamily: "Rubik, sans-serif", color: item.statoColor, fontWeight: "bold" }}>
                          ● {item.stato}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontFamily: "Rubik, sans-serif", color: "text.secondary", mb: 1 }}>
                        N° Fattura: <Typography component="span" sx={{ fontWeight: "bold", color: "text.primary", fontFamily: "Rubik, sans-serif" }}>{item.numeroFattura}</Typography>
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: "Rubik, sans-serif", color: "text.secondary", mb: 1 }}>
                        Data Emissione: <Typography component="span" sx={{ fontWeight: "bold", color: "text.primary", fontFamily: "Rubik, sans-serif" }}>{item.dataEmissione}</Typography>
                      </Typography>
                      <Typography variant="h5" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold", color: "#0b3d87", mt: 2 }}>
                        {item.importo}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      {item.pdfUrl ? (
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<PictureAsPdf />}
                          onClick={() => window.open(item.pdfUrl, "_blank")}
                          sx={{
                            backgroundColor: "#0b3d87",
                            color: "white",
                            borderRadius: "20px",
                            fontWeight: "bold",
                            fontFamily: "Rubik, sans-serif",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#092b6a" },
                          }}
                        >
                          Scarica Documento
                        </Button>
                      ) : (
                        <Typography sx={{ fontFamily: "Rubik, sans-serif", color: "text.secondary", width: "100%", textAlign: "center" }}>
                          Nessun PDF
                        </Typography>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography sx={{ fontFamily: "Rubik, sans-serif", textAlign: "center", py: 5 }}>
              Nessuna fattura trovata.
            </Typography>
          )}
        </Box>
    </Box>
  );
};

export default Fatturazione;
